<script lang="ts" setup>
import Banner from '@/domains/esg/components/banner/banner.vue'
import {
  partnerships,
  ratings,
  distinctions,
  keyActivitiesBoxes,
  companyValues,
  BSPillars as BSPillarsData,
  moreAbout as moreAboutData,
} from '@/domains/esg/composables/content/responsibleBusiness'
import UiItems from '@/components/UiItem/UiItems.vue'
import BusinessModel from '~/domains/esg/components/BusinessModel/BusinessModelV2.vue'
import BSPillars from '~/domains/esg/components/BSPillars/BSPillars.vue'
import MoreAbout from '~/domains/esg/components/MoreAbout/MoreAbout.vue'
import Partnerships from '~/domains/esg/components/Partnerships/Partnerships.vue'
import ContactForm from '~/domains/esg/components/ContactForm/ContactForm.vue'

const { t, locale } = useI18n()
const keyActivitiesBoxesTranslated = keyActivitiesBoxes.map(box => ({
  ...box,
  title: t(box.title),
  text: t(box.text),
  image: {
    ...box.image,
    alternative: t(box.image.alternative),
    url: t(box.image.url),
  },
}))

useHead({
  title: t('pageTitles.responsibleBusiness.title'),
  meta: [
    {
      name: 'description',
      content: t('pageTitles.responsibleBusiness.meta'),
    },
  ],
  link: [
    {
      hid: 'favicon',
      rel: 'icon',
      type: 'image/x-icon',
      href: '/favicon.ico',
    },
    {
      rel: 'canonical',
      href: t(`pageTitles.responsibleBusiness.canonical.${locale.value}`),
    },
    {
      rel: 'alternate',
      hreflang: 'pl',
      href: t('pageTitles.responsibleBusiness.canonical.pl'),
    },
    {
      rel: 'alternate',
      hreflang: 'en',
      href: t('pageTitles.responsibleBusiness.canonical.en'),
    },
    {
      rel: 'alternate',
      hreflang: 'x-default',
      href: t('pageTitles.responsibleBusiness.canonical.pl'),
    },
  ],
})

</script>

<template>
  <div>
    <Hero
      desktop-image="/images/hero/benefitsystems-responsible-business-hero_d.webp"
      mobile-image="/images/hero/benefitsystems-responsible-business-hero_m.webp"
      :header="$t('responsibleBusiness.hero.header')"
      :subheader="$t('responsibleBusiness.hero.subheader')"
      :text="$t('responsibleBusiness.hero.text')"
      :button="{
        text: $t('responsibleBusiness.hero.button.text'),
        to: $t('responsibleBusiness.hero.button.link'),
        icon: '/icons/icon-download.svg',
        target: '_blank'
      }"
      :button2="{
        text: $t('responsibleBusiness.hero.button2.text'),
        to: $t('responsibleBusiness.hero.button2.link'),
        icon: '/icons/icon-download-dark.svg',
        variant: 'secondary',
        target: '_blank'
      }"
    />

    <TextWithImage
      id="section1"
      class="section1"
      :header="$t('responsibleBusiness.section1.header')"
      :text="$t('responsibleBusiness.section1.text')"
      :text-second="$t('responsibleBusiness.section1.textSecond')"
      :text-third="$t('responsibleBusiness.section1.textThird')"
      :text-third-extra="$t('responsibleBusiness.section1.textThirdExtra')"
      image="/images/responsible-business/benefitsystems-responsible-business-our-approach_d.webp"
      :button2="{
        text: $t('responsibleBusiness.section1.button2.text'),
        to: $t('responsibleBusiness.section1.button2.link'),
        icon: '/icons/icon-download-dark.svg',
        variant: 'secondary',
        target: '_blank'
      }"
    />

    <UiSection
      color="psychology-100"
      class="section8"
      :title="$t('responsibleBusiness.section8.header')"
      :text="$t('responsibleBusiness.section8.textSecond')"
    >
      <UiHeader
        :header="$t('responsibleBusiness.section8.header')"
        :subheader="$t('responsibleBusiness.section8.text')"
        position="center"
        :class="'section8__header'"
        header-size="body-1"
      />

      <UiItems
        :items="companyValues.items"
        layout="layout-1"
      />
    </UiSection>

    <MoreAbout
      :header="$t('responsibleBusiness.section1.moreAbout.header')"
      :items="moreAboutData.ourApproach"
      class="more-about"
    />

    <UiHeader
      :header="$t('responsibleBusiness.section2.header')"
      position="center"
      header-size="h4"
    />

    <Banner
      id="section2"
      class="section_banner"
      :heading="$t('responsibleBusiness.section2.heading')"
      :text="$t('responsibleBusiness.section2.text')"
      color="white"
      background="blue-400"
      :button="{
        text: $t('responsibleBusiness.section2.button.text'),
        to: $t('responsibleBusiness.section2.button.link'),
        icon: '/icons/icon-download.svg',
        target: '_blank'
      }"
    />

    <BSPillars
      :header="BSPillarsData.title"
      :subheader="BSPillarsData.subTitle"
      :items="BSPillarsData.items"
    />

    <MoreAbout
      :header="$t('responsibleBusiness.sectionPillars.moreAbout.header')"
      :items="moreAboutData.strategy"
      class="more-about"
    />

    <TextWithImage
      id="section3"
      class="section3"
      :header="$t('responsibleBusiness.section3.header')"
      :text="$t('responsibleBusiness.section3.text')"
      :text-second="$t('responsibleBusiness.section3.textSecond')"
      image="/images/responsible-business/benefitsystems-responsible-sustainable-development_d.webp"
      :button="{
        text: $t('responsibleBusiness.section3.button.text'),
        to: $t('responsibleBusiness.section3.button.link'),
        icon: '/icons/icon-download.svg',
        target: '_blank'
      }"
    />

    <BusinessModel
      :header="$t('responsibleBusiness.businessModel.header')"
      :subheader="$t('responsibleBusiness.businessModel.subheader')"
    />

    <MoreAbout
      :header="$t('responsibleBusiness.businessModel.moreAbout.header')"
      :items="moreAboutData.sustainableDevelopment"
      class="more-about"
    />

    <TextWithBoxes
      id="section5"
      class="section5"
      layout="layout-3"
      :header="$t('responsibleBusiness.section5.header')"
      :text="$t('responsibleBusiness.section5.text')"
      :boxes="keyActivitiesBoxesTranslated"
      color="gray"
    />

    <UiSection
      color="gray"
      class="section9"
    >
      <UiHeader
        :header="$t('responsibleBusiness.section9.header')"
        :subheader="$t('responsibleBusiness.section9.subheader')"
        :class="'section9__header'"
      />

      <UiButton
        :to="$t('responsibleBusiness.section9.button.link')"
        :variant="'primary'"
        :target="'_blank'"
      >
        {{ $t('responsibleBusiness.section9.button.text') }}
      </UiButton>
    </UiSection>

    <div class="more-about__wrapper">
      <MoreAbout
        :header="$t('responsibleBusiness.section5.moreAbout.header')"
        :items="moreAboutData.keyActivities"
        class="more-about more-about__key-activities"
      />
    </div>

    <Partnerships />

    <UiSection
      color="white"
      class="section7"
    >
      <UiItems
        :items="partnerships.items"
        :title="partnerships.title"
      />

      <UiItems
        :items="ratings.items"
        :title="ratings.title"
      />

      <UiItems
        :items="distinctions.items"
        :title="distinctions.title"
      />
    </UiSection>

    <MoreAbout
      :header="$t('responsibleBusiness.section6.moreAbout.header')"
      :items="moreAboutData.partnerships"
      class="more-about"
    />

    <ContactForm
      :header="$t('responsibleBusiness.contact.form.header')"
      color="gray"
      :badge="`/icons/badges/flag-4_${locale}.png`"
      form-id="esg-contact-form"
      :utm="{
        utmSource: $t('responsibleBusiness.contact.form.utm.utmSource'),
      }"
    />

  </div>
</template>

<style lang="scss" scoped>
.section1 {
  :deep(.text-with-image__actions .button) {
    align-self: flex-start;
  }

  @include atM {
    :deep(.ui-header) {
      margin-bottom: 5.6rem;
    }
  }
}

.section3 {
  :deep(.text-with-image__layout) {
    align-items: center;
  }

  :deep(.text-with-image__actions .button) {
    align-self: flex-start;
  }

  @include atM {
    :deep(.ui-header) {
      margin-bottom: 5.6rem;
    }

    :deep(.text-with-image__actions .button) {
      align-self: center;
    }
  }
}

.section6 {
  :deep(.ui-header) {
    @include atM {
      margin-bottom: 5.6rem;
    }
  }

  :deep(.text-with-image__text) {
    margin-bottom: 0;
  }
}

.section7 {
  padding-top: 0;
}

.section8 {
  border-radius: 0;

  &.section--psychology-100 {
    background-color: $psychology-100;
  }

  :deep(.ui-header) {
    @include h8;
    margin-bottom: 2.4rem;
  }

  :deep(.ui-header__subheader) {
    @include h10;
    margin-bottom: 3.2rem;
  }

  @include atM {
    border-radius: 1.6rem;

    :deep(.ui-header) {
      @include h4;
      margin-bottom: 4.8rem;
    }

    :deep(.ui-header__subheader) {
      @include h8;
      max-width: 132rem;
      margin: 0 auto 6.4rem;
    }
  }
}

.more-about {
  margin-bottom: 6rem;

  &__wrapper {
    background-color: $gray-100;
  }

  @include atM {
    margin-bottom: 12rem;
  }
}

.more-about__key-activities {
  border-bottom: none;

  :deep(.button--secondary) {
    background-color: $white;
    border-color: $gray-300;
  }
}

.section9 {
  padding-top: 0;
  
  :deep(.ui-header__subheader) {
    @include h10;
    margin-bottom: 3.2rem;
    text-align: left;
  }

  :deep(.button) {
    margin: 0 auto;
  }

  @include atM {
    :deep(.ui-header__subheader) {
      @include h9;
      max-width: 132rem;
      margin: 2.4rem auto 6.4rem;
      text-align: center;
    }

    :deep(.ui-header) {
      text-align: center;
    }
  }
}

.section_banner {
  align-items: flex-start;
  min-height: 608px;
  margin-top: 4rem;
  background: url('/images/responsible-business/benefitsystems-responsible-business-key-politics_m_2x.webp') no-repeat bottom right;
  background-size: cover;
  border-radius: 0;

  :deep(.banner__text) {
    max-width: 37.5rem;
  }

  :deep(.banner__heading) {
    margin-bottom: 2.4rem;
  }

  @include atXS {
    background: url('/images/responsible-business/benefitsystems-responsible-business-key-politics_m_2x.webp') no-repeat 0 75%;
    background-size: cover;
  }

  @include atM {
    align-items: center;
    min-height: 694px;
    margin-top: 9rem;
    margin-bottom: 1.6rem;
    background: url('/images/responsible-business/benefitsystems-responsible-business-key-politics_d.webp') no-repeat top center;
    background-size: cover;
    border-radius: 1.6rem;

    :deep(.banner__text) {
      max-width: 65%;
      margin-bottom: 4.8rem;
    }

    :deep(.banner__heading) {
      margin-bottom: 4.8rem;
    }
  }
}

.section5 {
  border-radius: 0;

  :deep(.ui-header) {
    text-align: left;
  }

  :deep(.text-with-boxes__text) {
    text-align: left;
  }

  @include atM {
    border-radius: 1.6rem;

    :deep(.ui-header) {
      text-align: center;
    }

    :deep(.text-with-boxes__text) {
      text-align: center;
    }
  }
}
</style>
